import { type CycleData, CycleOptions } from "@qendo/profile";
import { useEffect, useMemo, useState } from "react";
import { Platform, StyleSheet, View } from "react-native";
import { HEALTH_PROFILE_SECTIONS } from "~/app/(app)/(auth)/health-profile";
import { Button } from "~/components/Button";
import { SwitchField } from "~/components/form/SwitchField";
import { Avatar } from "~/components/layout/Avatar";
import { EditListingItem } from "~/components/layout/EditListingItem";
import { ListingGroup } from "~/components/layout/ListingGroup";
import { ListingItem } from "~/components/layout/ListingItem";
import { ConfirmationModal } from "~/components/layout/modal/ConfirmationModal";
import { Page } from "~/components/layout/Page";
import { BaseText, Subtitle, Title } from "~/components/layout/text";
import { Can } from "~/components/permission/Can";
import { Nothing } from "~/components/permission/Nothing";
import { Only } from "~/components/permission/Only";
import { ToDo } from "~/components/permission/ToDo";
import { QENDO_LINKS } from "~/constants/environment";
import { useDismiss } from "~/providers/DismissProvider";
import { useProfile } from "~/providers/ProfileProvider";
import { useSession } from "~/providers/SessionProvider";
import { formatDateForHumans } from "~/util/dates";
import { plural } from "~/util/string";

type CycleKey = "tracked" | "estimate";
type CycleValue<K extends CycleKey> = CycleData[K];

export default function More() {
    const { logOut, is_share_user } = useSession();
    const { setReturnTo } = useDismiss();
    const { profile, cycle, updateCycleSettings, cannot, cannotAny } = useProfile();
    const [confirm, setConfirm] = useState(false);
    const [updating, setUpdating] = useState(false);
    const [tracked, setTracked] = useState(cycle.tracked);
    const [estimate, setEstimate] = useState(cycle.tracked && cycle.estimate);

    const joined = useMemo(
        () => (profile.created_at ? formatDateForHumans(profile.created_at, "approximate") : null),
        [profile.created_at],
    );

    const setCycle = <K extends CycleKey, V extends CycleValue<K>>(key: K, value: V) => {
        if (updating) {
            return;
        }

        setUpdating(true);

        if (key === "tracked") {
            setTracked(value);
        } else {
            setEstimate(value);
        }

        updateCycleSettings({
            ...cycle,
            [key]: value,
        }).finally(() => setUpdating(false));
    };

    useEffect(() => {
        setTracked(cycle.tracked);
        setEstimate(cycle.estimate);
    }, [cycle.tracked, cycle.estimate]);

    const cycle_length = CycleOptions.find(({ value }) => value === cycle.cycle)?.label;

    return (
        <>
            <Page gradient tabs gap={24} showVersion>
                <View style={styles.profile}>
                    <Avatar size={64} />
                    <View>
                        <Title>{profile?.name ?? "Jane Smith"}</Title>
                        <Only when={joined}>
                            <Subtitle>Joined {joined}</Subtitle>
                        </Only>
                    </View>
                </View>
                <ListingGroup
                    title="Health"
                    keyExtractor={({ href }) => href as string}
                    items={[
                        {
                            href: "/health-profile",
                            icon: "health-profile",
                            title: "Health profile",
                            hide: cannotAny("view", HEALTH_PROFILE_SECTIONS),
                        },
                        {
                            href: "/more/care-team",
                            icon: "care-team",
                            title: "Care team",
                            hide: cannot("view", "team"),
                        },
                        {
                            href: "/emergency-information",
                            icon: "ambulance",
                            title: "Emergency information",
                            hide: cannot("view", "emergency"),
                        },
                        {
                            href: "/more/reports",
                            icon: "report",
                            title: "Reports",
                            hide: cannotAny("view", ["activities", "symptoms", "journal"]),
                        },
                    ]}
                />
                <Can section="cycle" ability="update">
                    <ListingGroup title="Cycle">
                        <ListingItem icon="cycle" hideChevron last={!tracked}>
                            <SwitchField
                                label="Track cycle and ovulation"
                                value={tracked}
                                onValueChange={(value) => setCycle("tracked", value)}
                            />
                        </ListingItem>
                        <Only when={tracked}>
                            <EditListingItem href="/on-boarding/cycle-length" onPress={() => setReturnTo("/more")}>
                                <BaseText size="md">Cycle length: {cycle_length}</BaseText>
                                <BaseText size="md">
                                    Period length: {cycle.period} {plural("day", cycle.period)}
                                </BaseText>
                            </EditListingItem>
                            <ListingItem icon="sparkle" hideChevron last>
                                <SwitchField
                                    label="Cycle prediction"
                                    value={estimate}
                                    onValueChange={(value) => setCycle("estimate", value)}
                                    hint="Predict upcoming cycles based on previous periods."
                                />
                            </ListingItem>
                        </Only>
                    </ListingGroup>
                    <Subtitle>
                        We use your cycle and period length settings to make predictions. When you log your period
                        regularly, we base predictions on your entries.
                    </Subtitle>
                </Can>
                <ListingGroup title="Account">
                    <ListingItem href="/more/account/account-details" icon="profile" title="Account details" />
                    <Only not when={is_share_user}>
                        <ListingItem href="/more/account/teams" icon="family-time" title="Teams" />
                        {Platform.select({
                            native: (
                                <ListingItem href="/more/account/notifications" icon="bell" title="Notifications" />
                            ),
                            default: <Nothing />,
                        })}
                        <ToDo until="Apple Health">
                            <ListingItem icon="health-app" title="Apple Health" />
                        </ToDo>
                        <ListingItem href="/more/account/app-security" icon="lock" title="App security" />
                    </Only>
                    <ListingItem href="/more/account/appearance" icon="sun" title="App appearance" last />
                </ListingGroup>
                <ListingGroup title="Help and feedback">
                    <ListingItem
                        icon="headset"
                        title="Contact us"
                        subtitle="We’re here to help"
                        href={QENDO_LINKS.contact}
                    />
                    <ListingItem
                        icon="feedback"
                        title="Feedback"
                        subtitle="Help us improve the app"
                        href={QENDO_LINKS.feedback}
                        last
                    />
                </ListingGroup>
                <ListingGroup title="More information">
                    <ListingItem title="About QENDO" href={QENDO_LINKS.about} />
                    <ListingItem title="Privacy policy" href={QENDO_LINKS.privacy} />
                    <ListingItem title="Terms of use" href={QENDO_LINKS.terms} />
                    <ListingItem title="Report" href={QENDO_LINKS.report} last />
                </ListingGroup>
                <View>
                    <Button title="Log out" variant="secondary" size="lg" onPress={() => setConfirm(true)} />
                </View>
            </Page>
            <Only when={confirm}>
                <ConfirmationModal
                    onConfirm={() => logOut()}
                    confirmVariant="destructive-quiet"
                    onRequestClose={() => setConfirm(false)}
                    title="Are you sure that you want to log out?"
                    description="All security settings and notifications will be lost."
                    confirmText="Log out"
                />
            </Only>
        </>
    );
}

const styles = StyleSheet.create({
    profile: {
        flexDirection: "row",
        alignItems: "center",
        gap: 16,
    },
});
